import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Modal,
  ListSubheader,
  TextField,
  Typography,
  Tooltip,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import makeStyles from "@material-ui/styles/makeStyles";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from "@mui/icons-material/Search";
import { z, object, any, string, date } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import Flatpickr from "react-flatpickr";
import { useDispatch } from "react-redux";

import FormInput from '../form/FormInput';
import { createUserFromSuperadmin } from "../../redux/actions/user";
import { resizeUserAvatarImage, resizeThumbnailImage } from "../../utils/resizeFile";
import config from "../../config/config";

import IMG_USER_PLACEHOLDER from "../../assets/img/user-placeholder.jpg";

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PRODUCT_TYPES = [
  "Trial User",
  "Pro User"
];
const containsText = (text, searchText) => text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;


export default function CreateNewUser({
  visible,
  onClose,
  onCreatedTicketSuccessed,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const useStyles = makeStyles({
    menuItem: {
      color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
      fontSize: '13px',
      "&:hover": {
        color: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      },
    },
    menuItemSelected: {
      color: '#fff',
      backgroundColor: '#009ef7 !important',
      alignItems: 'center',
      justifyContent: 'space-between',
      "&:hover": {
        color: '#fff',
        backgroundColor: '#009ef7 !important',
      },
    },
  });
  const styles = useStyles();

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    expireDate: '',
    city: '',
    country: ''
  };

  const createUserSchema = object({
    firstName: string().min(1, 'First Name is required').max(30, 'First Name must be less than 30 letters'),
    lastName: string().min(1, 'Last Name is required').max(30, 'Last Name must be less than 30 letters'),
    email: string().min(1, 'Email is required').email('Email is invalid'),
    username: string().min(1, 'Username is required').max(30, 'Username must be less than 30 letters'),
    password: string(),
    membership: string(),
    expireDate: date({
      required_error: "Please select a date and time",
      invalid_type_error: "That's not a date!"
    }),
    country: string(),
    city: any(),
    profilePhoto: z
      .any()
      .refine((files) => files?.length === 1, "Image is required.")
      .refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, `Max file size is 5MB.`)
      .refine(
        (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
        ".jpg, .jpeg, .png and .webp files are accepted."
      )
  });
  const methods = useForm({
    resolver: zodResolver(createUserSchema),
    defaultValues
  });

  const { register, formState: { errors } } = methods;

  const watchFeaturedImage = methods.watch('profilePhoto', []);

  useEffect(() => {
    handleSelectProfilePhotoFile(watchFeaturedImage);
  }, [watchFeaturedImage]);

  const [membership, setMembership] = useState('');
  const [pickrDate, setPickrDate] = useState();
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('');
  const [searchText, setSearchText] = useState("");
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoFileSelected, setProfilePhotoFileSelected] = useState(null);
  const [profilePhotoThumbnailFile, setProfilePhotoThumbnailFile] = useState(null);

  useEffect(() => {
    getCountries();
  }, []);

  const getCountries = async () => {
    const { data } = await fetch(config.COUNTRY_URL).then(response => response.json());
    setCountries(data);
  }
  const displayedCountries = useMemo(
  () => countries.filter((option) => containsText(option.country, searchText)),
    [searchText, countries]
  );

  const onChangeCountry = (e) => {
    setCountry(e.target.value);
    methods.setValue("country", e.target.value);
  }


  const onChangeMembership = (e) => {
    setMembership(e.target.value);
    methods.setValue("membership", e.target.value);
  }

  // ? Form Handler
  const onSubmitHandler = async (data) => {
    let formData = new FormData();
    formData.append("firstName", data.firstName);
    formData.append('lastName', data.lastName);
    formData.append('email', data.email);
    formData.append("username", data.username);
    formData.append("password", data.password);
    formData.append("membership", data.membership);
    formData.append("expireDate", data.expireDate);
    formData.append("profilePhoto", profilePhotoFile);
    formData.append("profilePhotoThumbnail", profilePhotoThumbnailFile);
    const { user } = await dispatch(createUserFromSuperadmin(formData));
    if (user) {
      setMembership('');
      setPickrDate();
      setCountry('');
      setSearchText('');
      setProfilePhotoFile(null);
      setProfilePhotoThumbnailFile(null);
      methods.reset();
      onCreatedTicketSuccessed();
    }
  };

  const handleSelectProfilePhotoFile = async files => {
    if (files.length === 0) {
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type)) {
      return;
    }
    if (files[0].size > MAX_FILE_SIZE) {
      return;
    }
    const file = files[0];
    const resizedFile = await resizeUserAvatarImage(file, file.type.split('/').pop().toUpperCase());
    setProfilePhotoFile(resizedFile);
    const thumbnailFile = await resizeThumbnailImage(file, file.type.split('/').pop().toUpperCase());
    setProfilePhotoThumbnailFile(thumbnailFile);
    const reader = new FileReader();
    reader.readAsDataURL(resizedFile);
    reader.onloadend = (e) => {
      setProfilePhotoFileSelected(reader.result);
    };
  }

  const handleUserPhotoUpload = () => {
    document.querySelector('#Profile-Photo-Selection').value = null;
    document.querySelector('#Profile-Photo-Selection').click();
  }

  const handleUserPhotoRemove = () => {
    setProfilePhotoFile(null);
    setProfilePhotoFileSelected(null);
    setProfilePhotoThumbnailFile(null);
  }

  return (
    <Modal
      open={visible}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: '650px'},
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          maxHeight: '95%',
          overflowY: 'auto',
          '::-webkit-scrollbar': {
            width: '12px',
          },
          '::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            borderRadius: '6px',
            border: '3px solid transparent',
            backgroundClip: 'content-box',
            backgroundColor: '#c1c1c1',
          },
          '&:focus-visible': {
            outline: 'none',
          }
        }}>
        <Grid sx={{ margin: 'auto', p: 4 }} className="text-center">
          <CloseIcon
            onClick={onClose}
            sx={{
              color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
              position: 'absolute',
              right: '16px',
              top: '16px', 
              display: { md: 'none' }
            }}
          />
          <Typography id="modal-modal-title" sx={{ fontSize: '24px', fontWeight: 800, textAlign: 'center' }}>
            Create New Member
          </Typography>
          <FormProvider {...methods}>
            <Box
              display='flex'
              flexDirection='column'
              component='form'
              noValidate
              autoComplete='off'
              sx={{ mt: 2 }}
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <Grid>
                <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>
                  Member Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormInput
                      placeholder='First Name'
                      type='text'
                      name='firstName'
                      fullWidth
                      required
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormInput
                      placeholder='Last Name'
                      type='text'
                      name='lastName'
                      fullWidth
                      required
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: '500', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', textAlign: 'left', mt: 1 }}>
                  Email <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormInput
                  placeholder='Enter Email'
                  type='text'
                  name='email'
                  fullWidth
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', mt: 1 }}>
                    Username <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <FormInput
                    placeholder='Set username'
                    type='text'
                    name='username'
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', mt: 1 }}>
                    Password <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <FormInput
                    placeholder='Set password'
                    type='text'
                    name='password'
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', mt: 1 }}>
                    Membership Type <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Select
                    fullWidth
                    inputProps={{ sx: { color: '#5e6278', fontWeight: 600 } }}
                    displayEmpty={true}
                    value={membership}
                    renderValue={value => value ? value : 'Select...'}
                    sx={{ mt: 1, textAlign: 'left', padding: 0, '.MuiSelect-select': { padding: '12px 20px' }, '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
                    onChange={onChangeMembership}>
                    {PRODUCT_TYPES.map((item, index) => (
                      <MenuItem
                        key={item}
                        value={item}
                        selected={item === membership}
                        classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}>
                        {item}
                        {item === membership && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', my: 1 }}>
                    Expiry Date <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Flatpickr
                    data-enable-time
                    value={pickrDate}
                    options={{
                      minDate: new Date(),
                    }}
                    onChange={([date]) => {
                      setPickrDate(date);
                      methods.setValue("expireDate", date);
                    }}
                    render={({ defaultValue, value, ...props }, ref) => {
                      return <input {...props} defaultValue={defaultValue} ref={ref} style={{
                        fontFamily: 'Poppins, Helvetica, sans-serif',
                        backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#1b1b29',
                        padding: '10px 20px',
                        color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
                        fontSize: '14px',
                        width: '100%',
                        boxSizing: 'border-box',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '600',
                      }} />
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', mt: 1 }}>
                    City
                  </Typography>
                  <FormInput
                    placeholder='Enter city'
                    type='text'
                    name='city'
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff', mt: 1 }}>
                    Country <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Select
                    fullWidth
                    inputProps={{
                      sx: {
                        color: theme.palette.mode === 'light' ?
                          country ? '#5e6278' : '#bdc0ca' :
                          country ? '#fff' : '#3c3e50',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        borderRadius: '6px',
                        padding: '11.3px 20px',
                        textAlign: 'left',
                      }
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: '300px' } } }}
                    displayEmpty={true}
                    renderValue={value => value ? value : 'Select...'}
                    sx={{
                      mt: 1,
                      padding: 0,
                      '.MuiSelect-select': { paddingY: '11.3px' },
                      '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
                    }}
                    value={country}
                    onChange={onChangeCountry}>
                    <ListSubheader>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Type to search..."
                        fullWidth
                        sx={{ margin: '5px 0px' }}
                        inputProps={{
                          style: {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                          }
                        }}
                        InputProps={{
                          style: {
                            padding: '10px 9px',
                            fontSize: '12px',
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          )
                        }}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") {
                            // Prevents autoselecting item while typing (default Select behaviour)
                            e.stopPropagation();
                          }
                        }}
                      />
                    </ListSubheader>
                    {displayedCountries.map((row, index) => (
                      <MenuItem
                        key={index}
                        value={row.country}
                        selected={row.country === country}
                        classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                      >
                        {row.country}
                        {row.country === country && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
              <Grid container sx={{ mt: 2, textAlign: 'left', display: 'flex' }}>
                <Grid item xs={4}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
                  Profile Photo <span style={{ color: 'red' }}>*</span>
                </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Box display="inline-flex" position="relative">
                    {profilePhotoFileSelected ?
                      <Box
                        component="img"
                        src={profilePhotoFileSelected}
                        className="Avatar-Image"
                        sx={{ width: '100px', height: '100px' }}
                        alt="avatarImageSelcted"
                      /> :
                      <Box
                        component="img"
                        src={IMG_USER_PLACEHOLDER}
                        className="Avatar-Image"
                        sx={{ width: '100px', height: '100px' }}
                        alt="avatar"
                      />
                    }
                    <Tooltip title="Add Profile Photo" placement="top" enterTouchDelay={0}>
                      <span className="Image-upload" onClick={handleUserPhotoUpload}>
                        <i className="fa fa-pen"></i>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove Profile Photo" placement="top" enterTouchDelay={0}>
                      <span className="Image-remove" onClick={handleUserPhotoRemove}>
                        <i className="fa fa-remove"></i>
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ color: '#a1a5b7', fontSize: '12px', textAlign: 'left', mt: 1 }}>
                    Recommended size 600x600px
                  </Typography>
                  {errors['profilePhoto'] && (
                    <p style={{ color: '#d32f2f', fontWeight: 400, fontSize: '0.75rem', marginInlineStart: '14px' }}>
                      {errors['profilePhoto']?.message}
                    </p>
                  )}
                </Grid>
                <input type="file" name="profile-photo" id="Profile-Photo-Selection" style={{ visibility: 'hidden' }} {...register('profilePhoto')} />
              </Grid>
              <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ padding: '11px 35px', fontSize: '14px', lineHeight: '21px', fontWeight: 600, textTransform: 'none', color: '#fff' }}>
                  Save
                </Button>
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                    padding: '11px 30px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#6d6d80',
                    marginInlineStart: 1,
                    '&:hover ': {
                      backgroundColor: theme.palette.mode === 'light' ? '#eff2f5' : '#323248',
                    }
                  }}>
                  Cancel
                </Button>
              </Grid>
            </Box>
          </FormProvider>
        </Grid>
      </Box>
    </Modal>
  );
}
